import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram } from "lucide-react";
import { HANDSTYLE, NAV_INSTAGRAM_URL } from "../lib/brand";
import { useNavItems } from "../lib/nav";

export default function Nav() {
  const links = useNavItems();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const series = new URLSearchParams(location.search).get("series");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 left-0 w-full z-50 transition-colors duration-500"
      style={{
        background: scrolled ? "rgba(247,245,240,0.82)" : "transparent",
        backdropFilter: scrolled ? "blur(14px)" : "none",
        borderBottom: scrolled ? "1px solid var(--line)" : "1px solid transparent",
      }}
    >
      <nav className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="RCKT — home" className="inline-flex items-center">
          <img src={HANDSTYLE} alt="RCKT" className="h-9 md:h-10 w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className="font-display text-xs tracking-[0.14em] transition-opacity duration-300 hover:opacity-50"
              style={{ opacity: l.match(pathname, series) ? 1 : 0.72 }}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={NAV_INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-instagram"
            aria-label="RCKT on Instagram"
            className="transition-opacity duration-300 hover:opacity-50"
            style={{ opacity: 0.72 }}
          >
            <Instagram size={16} strokeWidth={1.6} />
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden font-display text-xs tracking-[0.14em]"
          aria-label="Menu"
        >
          {open ? "CLOSE" : "MENU"}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t hairline" style={{ background: "var(--paper)" }} data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="font-display text-sm tracking-[0.14em]">
                {l.label}
              </Link>
            ))}
            <a href={NAV_INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="nav-instagram-mobile"
              aria-label="RCKT on Instagram" className="inline-flex items-center gap-2 font-display text-sm tracking-[0.14em]">
              <Instagram size={16} strokeWidth={1.6} /> Instagram
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
